import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import { FilterBar, FilterBarField, FilterBarOption } from "design-system/molecules/FilterBar"
import { useCompanyFilterField } from "modules/backoffice/hooks/companies/useCompanyFilterField"
import { BACKOFFICE_RECENT_TIME_FRAME_OPTIONS } from "modules/backoffice/lib/backofficeRecentTimeFrameOptions"
import { TimeRangeSelector } from "modules/shared/components/TimeRange/TimeRangeSelector"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { useFilterBarLabels } from "modules/shared/hooks/useFilterBarLabels"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  BackofficeFlagsCampaignsResponseDataSchema,
  BackofficeFlagsKindEnum,
  ChannelEnum,
  DataResponse,
  TimeFrame,
} from "schema"
import { z } from "zod"

type Campaigns = z.infer<typeof BackofficeFlagsCampaignsResponseDataSchema>
type FlagKind = (typeof BackofficeFlagsKindEnum)[number]
type Channel = (typeof ChannelEnum)[keyof typeof ChannelEnum]

type Props = {
  query: string
  setQuery: (query: string) => void
  selectedTimeFrame: TimeFrame
  onTimeFrameChange: (timeFrame: TimeFrame) => void
  selectedCompanyId: string | undefined
  onCompanyChange: (companyId: string | undefined) => void
  selectedCampaignId: string | undefined
  onCampaignChange: (campaignId: string | undefined) => void
  selectedChannel: Channel | undefined
  onChannelChange: (channel: Channel | undefined) => void
  selectedKind: FlagKind | undefined
  onKindChange: (kind: FlagKind | undefined) => void
  selectedNamespace: string | undefined
  namespaceOptions: FilterBarOption[]
  onNamespaceChange: (namespace: string | undefined) => void
  selectedKeys: string[]
  keyOptions: FilterBarOption[]
  onKeysChange: (keys: string[]) => void
  shouldIncludeLegacy: boolean
  onIncludeLegacyChange: (shouldIncludeLegacy: boolean) => void
  onClearFilters: () => void
}

export function BackofficeFlagsFilters(props: Props) {
  const { t } = useTranslation()
  const labels = useFilterBarLabels()
  const [campaignSearch, setCampaignSearch] = useState("")
  const debouncedCampaignSearch = useDebouncedValue({ value: campaignSearch })

  const companyField = useCompanyFilterField({
    label: t("backoffice.flags.filters.fields.company"),
    value: props.selectedCompanyId,
    onChange: props.onCompanyChange,
  })

  const campaignQuery = debouncedCampaignSearch || props.selectedCampaignId
  const { data: campaigns = [] } = useQuery({
    queryKey: ["backofficeFlagsCampaigns", campaignQuery],
    queryFn: async () => {
      const { data } = await axios.get<DataResponse<Campaigns>>("/backoffice/flags/campaigns", {
        params: { query: campaignQuery },
      })
      return data.data
    },
  })

  const fields: FilterBarField[] = [
    companyField,
    {
      key: "campaignId",
      type: "select",
      label: t("backoffice.flags.filters.fields.campaign"),
      value: props.selectedCampaignId,
      onChange: props.onCampaignChange,
      optionsSearch: campaignSearch,
      onOptionsSearchChange: setCampaignSearch,
      options: campaigns.map((campaign) => ({
        value: campaign.id,
        label: campaign.name ?? t("campaigns.untitled"),
      })),
    },
    {
      key: "channel",
      type: "select",
      label: t("backoffice.flags.filters.fields.channel"),
      value: props.selectedChannel,
      onChange: (value) =>
        props.onChannelChange(Object.values(ChannelEnum).find((channel) => channel === value)),
      options: Object.values(ChannelEnum).map((channel) => ({
        value: channel,
        label: t(`campaigns.channel.${channel.toLowerCase()}.label`),
      })),
    },
    {
      key: "kind",
      type: "select",
      label: t("backoffice.flags.filters.fields.kind"),
      value: props.selectedKind,
      onChange: (value) =>
        props.onKindChange(BackofficeFlagsKindEnum.find((kind) => kind === value)),
      options: BackofficeFlagsKindEnum.map((kind) => ({ value: kind, label: kind })),
    },
    {
      key: "namespace",
      type: "select",
      label: t("backoffice.flags.filters.fields.namespace"),
      value: props.selectedNamespace,
      onChange: props.onNamespaceChange,
      options: props.namespaceOptions,
    },
    {
      key: "key",
      type: "multiSelect",
      label: t("backoffice.flags.filters.fields.key"),
      value: props.selectedKeys,
      onChange: (keys) => props.onKeysChange(keys ?? []),
      options: props.keyOptions,
    },
    {
      key: "includeLegacy",
      type: "select",
      label: t("backoffice.flags.filters.includeLegacy"),
      value: props.shouldIncludeLegacy ? "true" : undefined,
      onChange: (value) => props.onIncludeLegacyChange(value === "true"),
      options: [{ value: "true", label: t("shared.filterBar.yes") }],
    },
  ]

  return (
    <FilterBar
      className="w-full"
      fields={fields}
      labels={labels}
      onClearFilters={props.onClearFilters}
      searchQuery={props.query || undefined}
      onSearchQueryChange={props.setQuery}
      searchPlaceholder={t("backoffice.flags.filters.searchPlaceholder")}
      trailing={
        <TimeRangeSelector
          value={props.selectedTimeFrame}
          onChange={({ timeFrame }) => props.onTimeFrameChange(timeFrame)}
          presets={BACKOFFICE_RECENT_TIME_FRAME_OPTIONS}
        />
      }
    />
  )
}
